import React, { useEffect } from 'react'
import { Helmet } from 'react-helmet'
import { useDispatch, useSelector } from 'react-redux'
import {
  Text,
  Spacer,
  Loader,
  Container,
  Box,
  Button,
  Calendar,
  TimeRange,
  Time,
  Faq,
  api,
  Icon,
  TextColor,
} from 'roadie-ui'
import Page from 'rui/widgets/Page'
import DriveSidebar from 'components/layout/DriveSidebar'
import ClaimPsst from '../Seattle/ClaimPsst'
import { getSeattleData } from 'util/server/seattle'
import { WeeklyStatement, SeattleData, SeattleDay } from 'types/seattle'
import { capitalize, formatCurrency } from 'util/format'
import { Redirect } from 'react-router-dom'
import { AdaptiveExternalLink } from 'components/layout/links'
import { AccountSetting } from 'types/profile'
import { getGoogleTranslateTargetLanguage, TextMap, translateText } from 'util/translate'
import { getLatestAccountSetting } from 'util/profile'
import {
  createLocalizedTranslations,
  getEnglishTextMap,
  hasLocalMessagesForLanguagePreference,
  LocalizedRichText,
  useLocalizedTranslations,
  useLocalization,
  LocalizedText,
} from 'localization'

const SeattleDashboardTextKeys = {
  Section1Title: 'dweb_seattle_dashboard_section1_title',
  DaysAvailable: 'dweb_seattle_dashboard_days_available',
  PerDay: 'dweb_common_per_day',
  FAQ1: 'dweb_seattle_dashboard_faq1',
  FAQ1Answer: 'dweb_seattle_dashboard_faq1_answer',
  FAQ2: 'dweb_seattle_dashboard_faq2',
  FAQ2Answer: 'dweb_seattle_dashboard_faq2_answer',
  FAQ2AnswerCaption1: 'dweb_seattle_dashboard_faq2_answer_caption1',
  FAQ2AnswerCaption2: 'dweb_seattle_dashboard_faq2_answer_caption2',
  FAQ2AnswerCaption3: 'dweb_common_per_day',
  FAQ3: 'dweb_seattle_dashboard_faq3',
  FAQ3Answer: 'dweb_seattle_dashboard_faq3_answer',
  FAQHereLinkText: 'dweb_common_here',
  FAQ4: 'dweb_seattle_dashboard_faq4',
  FAQ4Answer: 'dweb_seattle_dashboard_faq4_answer',
  FAQ5: 'dweb_seattle_dashboard_faq5',
  FAQ5AnswerCovered: 'dweb_seattle_dashboard_faq5_answer_covered',
  FAQ5AnswerNotCovered: 'dweb_seattle_dashboard_faq5_answer_not_covered',
  Legend: 'dweb_seattle_dashboard_legend',
  LegendItem1: 'dweb_seattle_dashboard_legend_item1',
  LegendItem2: 'dweb_seattle_dashboard_legend_item2',
  ModalTitle: 'dweb_seattle_dashboard_modal_title',
  Reason: 'dweb_seattle_dashboard_reason',
  ModalLabel: 'dweb_seattle_dashboard_modal_label',
  PreviouslyRedeemed: 'dweb_seattle_dashboard_previously_redeemed',
  ModalReason1: 'dweb_seattle_dashboard_modal_reason1',
  ModalReason2: 'dweb_seattle_dashboard_modal_reason2',
  ModalReason3: 'dweb_seattle_dashboard_modal_reason3',
  ModalReason4: 'dweb_seattle_dashboard_modal_reason4',
  ModalReason5: 'dweb_seattle_dashboard_modal_reason5',
  ModalSubmit: 'dweb_common_submit',
  Section2Title: 'dweb_seattle_dashboard_section2_title',
  Section2EmptySubtitle: 'dweb_seattle_dashboard_section2_empty_subtitle',
  Section2EmptyCaption: 'dweb_seattle_dashboard_section2_empty_caption',
} as const

const SeattleDashboardStaticText: TextMap = getEnglishTextMap(SeattleDashboardTextKeys)

const SeattleDashboardValidator: React.FC = () => {
  const seattleDriver = useSelector<any, Record<string, any>>((state) => state.seattleDriver)

  if (seattleDriver) {
    return <SeattleDashboardPreloader />
  } else {
    return <Redirect to="/dashboard" />
  }
}

const SeattleDashboardPreloader: React.FC = () => {
  const { t } = useLocalization()
  const { profile, weeklyStatements } = useSelector<
    any,
    {
      profile: Record<string, any>
      weeklyStatements: WeeklyStatement[]
    }
  >((state) => ({
    profile: state.profile,
    weeklyStatements: state.seattleEarningsStatement,
  }))
  const [seattleData, setSeattleData] = React.useState<SeattleData | null>(null)
  const fetchSeattleData = React.useCallback(() => {
    getSeattleData(profile.id, weeklyStatements).then(setSeattleData)
  }, [profile.id, weeklyStatements])

  React.useEffect(() => {
    if (seattleData) {
      return
    }
    fetchSeattleData()
  }, [fetchSeattleData, seattleData])

  return (
    <Page sidebar={<DriveSidebar />}>
      <Helmet>
        <title>{t('dweb_layout_drive_sidebar_seattle_driver_dashboard')}</title>
      </Helmet>
      {seattleData ? <SeattleDashboardPage seattleData={seattleData} refetchData={fetchSeattleData} /> : <Loader />}
    </Page>
  )
}

const SeattleDashboardPage: React.FC<{
  seattleData: SeattleData
  refetchData: VoidFunction
}> = (props) => {
  const localizedTranslations = useLocalizedTranslations(SeattleDashboardTextKeys)
  const localizedStaticText = localizedTranslations.map
  const { seattleData, refetchData } = props
  const isMobile = useSelector<any, boolean>((state) => state.config.mediaSize === 'mobile')
  const dispatch = useDispatch()
  const perDayStatistic = seattleData.earningsPerDay.toFixed(2)

  const driverHasSeattleCoveredCert = useSelector<any, boolean>(
    (state) =>
      Array.isArray(state.trainingCertifications.delivery_certifications_drivers) &&
      state.trainingCertifications.delivery_certifications_drivers.some(
        (cert: { name?: string }) => cert.name === 'seattle_ordinance_protected_driver'
      )
  )
  const accountSettings = useSelector<any, AccountSetting[]>((state) => state.accountSettings)
  const languagePreference = getLatestAccountSetting(accountSettings, 'preferred_language') || 'english'
  const [translationMap, setTranslationMap] = React.useState(localizedStaticText)
  const translations = React.useMemo(
    () => createLocalizedTranslations(SeattleDashboardTextKeys, translationMap),
    [translationMap]
  )
  const [loadingTranslation, setLoadingTranslation] = React.useState(false)
  const [currentLanguage, setCurrentLanguage] = React.useState<string>('english')
  const googleTranslateTargetLanguage = getGoogleTranslateTargetLanguage(languagePreference)
  const shouldOfferGoogleTranslation =
    Boolean(googleTranslateTargetLanguage) && !hasLocalMessagesForLanguagePreference(languagePreference)

  const ClaimPsstStaticTextMap: TextMap = {
    Title: translations.ModalTitle,
    Label: translations.ModalLabel,
    Reason: translations.Reason,
    Reason1: translations.ModalReason1,
    Reason2: translations.ModalReason2,
    Reason3: translations.ModalReason3,
    Reason4: translations.ModalReason4,
    Reason5: translations.ModalReason5,
    Submit: translations.ModalSubmit,
    PreviouslyRedeemed: translations.PreviouslyRedeemed,
  }

  const CalendarLegendTextMap: TextMap = {
    Title: translations.Legend,
    Item1: translations.LegendItem1,
    Item2: translations.LegendItem2,
  }

  const WeeklyStatementsTextMap: TextMap = {
    EmptySubtitle: translations.Section2EmptySubtitle,
    EmptyCaption: translations.Section2EmptyCaption,
  }

  useEffect(() => {
    const handleTranslate = async () => {
      setLoadingTranslation(true)
      if (currentLanguage != 'english') {
        setTranslationMap(localizedStaticText)
        setCurrentLanguage('english')
        setLoadingTranslation(false)
        dispatch({
          type: 'CLEAR_ALL_FLASH',
        })
        return
      } else {
        if (!googleTranslateTargetLanguage) {
          setLoadingTranslation(false)
          return
        }

        try {
          const translatedMap = await translateText(localizedStaticText, googleTranslateTargetLanguage)
          setTranslationMap(translatedMap)
          setCurrentLanguage(languagePreference)
          setLoadingTranslation(false)
          dispatch({
            type: 'CLEAR_ALL_FLASH',
          })
        } catch (error) {
          console.error('Translation failed:', error)
          dispatch({
            type: 'CLEAR_ALL_FLASH',
          })
          dispatch({
            type: 'PUSH_FLASH',
            flash: {
              message: (
                <Text.Body1 $color="neutral-white">
                  <LocalizedText translationKey="dweb_translation_failed_try_again" />
                </Text.Body1>
              ),
              type: 'error',
            },
          })
        }
      }
    }

    if (shouldOfferGoogleTranslation) {
      dispatch({
        type: 'PUSH_FLASH',
        flash: {
          message: (
            <Container.Grid
              onClick={handleTranslate}
              $display="flex"
              $textAlign="center"
              $alignItems="center"
              $justifyContent="center"
              $gridGap="1rem"
            >
              <Icon.Translate $color="neutral-white" $width="2.4rem" />
              {loadingTranslation ? (
                <Text.Body1 $color="neutral-white">
                  <LocalizedText translationKey="dweb_delivery_details_consolidation_details_translating" />
                </Text.Body1>
              ) : currentLanguage === 'english' ? (
                <Text.Body1 $color="neutral-white">
                  <LocalizedRichText
                    translationKey="dweb_delivery_details_consolidation_details_tap_translate_strong_text1"
                    params={{ strongText1: <strong>{capitalize(languagePreference)}</strong> }}
                  />
                </Text.Body1>
              ) : (
                <Text.Body1 $color="neutral-white">
                  <LocalizedRichText
                    translationKey="dweb_delivery_details_consolidation_details_viewing_strong_text1_tap_revert_english"
                    params={{ strongText1: <strong>{capitalize(currentLanguage)}</strong> }}
                  />
                </Text.Body1>
              )}
            </Container.Grid>
          ),
          type: 'info',
        },
      })
    }
  }, [
    accountSettings,
    languagePreference,
    dispatch,
    currentLanguage,
    loadingTranslation,
    localizedStaticText,
    googleTranslateTargetLanguage,
    shouldOfferGoogleTranslation,
  ])

  useEffect(() => {
    if (currentLanguage === 'english') setTranslationMap(localizedStaticText)
  }, [currentLanguage, localizedStaticText])

  return (
    <>
      <Spacer $height="2.4rem" />
      <Text.Overline>{translations.Section1Title}</Text.Overline>
      <Spacer $height="0.8rem" />
      <Container.Panel>
        <Container.Grid $gridTemplateColumns="1fr 1px 1fr">
          <DashboardStatistic
            isMobile={isMobile}
            value={String(seattleData.psstDays.length)}
            label={translations.DaysAvailable}
          />
          <Box.Border $borderLeftWidth="1px" $borderLeftStyle="solid" $borderLeftColor="neutral-light" />
          <DashboardStatistic isMobile={isMobile} value={perDayStatistic} unit="$" label={translations.PerDay} />
        </Container.Grid>
        <Spacer $height="3.2rem" />
        <ClaimPsst
          psstDays={seattleData.psstDays}
          claimedDays={seattleData.seattleDays}
          onSubmit={refetchData}
          staticTextMap={ClaimPsstStaticTextMap}
        />
        <Spacer $height="3.2rem" />
        <SeattleCalendar isMobile={isMobile} seattleData={seattleData} staticTextMap={CalendarLegendTextMap} />
        <Spacer $height="4rem" />
        <Faq title={translations.FAQ1}>{translations.FAQ1Answer}</Faq>
        <Spacer $height="0.8rem" />
        <Faq title={translations.FAQ2}>
          {currentLanguage == 'english' ? (
            <LocalizedRichText
              translationKey="dweb_seattle_dashboard_average_daily_compensation_formula_rich"
              params={{
                earningsText: (
                  <b>
                    <LocalizedText translationKey="dweb_seattle_dashboard_total_earnings_from_all_covered_calendar_days_last" />
                  </b>
                ),
                daysText: (
                  <b>
                    <LocalizedText translationKey="dweb_seattle_dashboard_total_number_covered_calendar_days_last_365_days" />
                  </b>
                ),
              }}
            />
          ) : (
            translations.FAQ2Answer
          )}
          <Spacer $height="2.4rem" />
          <Container.Grid $justifyItems="center">
            <Container.Grid $gridTemplateColumns="repeat(5, 1fr)" $textAlign="center" $alignItems="center">
              <Box.Simple>
                <Text.Body2 $color="neutral-black">{formatCurrency(seattleData.earnings)}</Text.Body2>
                <Text.Caption $textTransform="uppercase">{translations.FAQ2AnswerCaption1}</Text.Caption>
              </Box.Simple>
              <Text.Caption>÷</Text.Caption>
              <Box.Simple>
                <Text.Body2 $color="neutral-black">{seattleData.daysWorked}</Text.Body2>
                <Text.Caption $textTransform="uppercase">{translations.FAQ2AnswerCaption2}</Text.Caption>
              </Box.Simple>
              <Text.Caption>=</Text.Caption>
              <Box.Simple>
                <Text.Body2 $color="neutral-black">{formatCurrency(perDayStatistic)}</Text.Body2>
                <Text.Caption $textTransform="uppercase">{translations.FAQ2AnswerCaption3}</Text.Caption>
              </Box.Simple>
            </Container.Grid>
          </Container.Grid>
        </Faq>
        <Spacer $height="0.8rem" />
        <Faq title={translations.FAQ3}>
          {translations.FAQ3Answer}
          <Button.A.Tertiary
            href={`${import.meta.env.REACT_APP_SUPPORT_URL}/support/s/article/Seattle-App-Based-Workers-Paid-Sick-and-Safe-Time-PSST-Ordinance-Policy-and-Procedure`}
            target="_blank"
            $color="primary-base"
            $textTransform="none"
          >
            {translations.FAQHereLinkText}
          </Button.A.Tertiary>
          .
        </Faq>
        <Spacer $height="0.8rem" />
        <Faq title={translations.FAQ4}>
          {translations.FAQ4Answer}
          <Button.A.Tertiary
            href="https://roadie-cdn.s3.us-east-1.amazonaws.com/seattle/ALL_OLS_AppBasedWorkers_NOR.pdf"
            target="_blank"
            $color="primary-base"
            $textTransform="none"
          >
            {translations.FAQHereLinkText}
          </Button.A.Tertiary>
          .
        </Faq>
        <Faq title={translations.FAQ5}>
          {driverHasSeattleCoveredCert ? translations.FAQ5AnswerCovered : translations.FAQ5AnswerNotCovered}
        </Faq>
      </Container.Panel>
      <Spacer $height="3.2rem" />
      <Text.Overline>{translations.Section2Title}</Text.Overline>
      <Spacer $height="0.8rem" />
      <Container.Panel $padding="0" $paddingMedia={{ mobile: '0' }}>
        <WeeklyStatements
          isMobile={isMobile}
          statements={seattleData.weeklyStatements}
          staticTextMap={WeeklyStatementsTextMap}
        />
      </Container.Panel>
    </>
  )
}

const DashboardStatistic: React.FC<{
  value?: string
  label?: string
  unit?: string
  isMobile: boolean
}> = (props) => {
  const { value, label, unit, isMobile } = props

  return (
    <Container.Grid $justifyItems="center">
      <Container.Position $position="relative" $color="neutral-black">
        {isMobile ? (
          <>
            <Text.H5>{value}</Text.H5>
            <Spacer $height="0.8rem" />
          </>
        ) : (
          <Text.H2>{value}</Text.H2>
        )}
        <Container.Position $position="absolute" $top="0" $right="100%">
          <Text.H5>{unit}</Text.H5>
        </Container.Position>
      </Container.Position>
      <Text.Body2>{label}</Text.Body2>
    </Container.Grid>
  )
}

const Jan_01_2024_date = new Date()
Jan_01_2024_date.setFullYear(2024, 0, 1)
const JAN_01_2024 = Time.fromDate(Jan_01_2024_date)

type CalendarDayInfo = {
  color: TextColor
  info: string
}

const SeattleCalendar: React.FC<{
  seattleData: SeattleData
  isMobile: boolean
  staticTextMap: TextMap
}> = (props) => {
  const { seattleData, isMobile, staticTextMap = SeattleDashboardStaticText } = props
  const { toLocalizedTime } = useLocalization()

  const CALENDARDAYINFO: Record<keyof SeattleDay, CalendarDayInfo> = React.useMemo(
    () => ({
      seattleGigCompleted: {
        color: 'primary-light',
        info: staticTextMap['Item1'],
      },
      psstUsed: {
        color: 'alert-base',
        info: staticTextMap['Item2'],
      },
    }),
    [staticTextMap]
  )

  const calendarInfo = React.useMemo(() => {
    return seattleData.seattleDays.valueMap((seattleDay) => {
      const calendarDayInfo: CalendarDayInfo[] = []

      if (seattleDay.seattleGigCompleted) {
        calendarDayInfo.push(CALENDARDAYINFO.seattleGigCompleted)
      }
      if (seattleDay.psstUsed) {
        calendarDayInfo.push(CALENDARDAYINFO.psstUsed)
      }

      return calendarDayInfo
    })
  }, [seattleData.seattleDays, CALENDARDAYINFO.seattleGigCompleted, CALENDARDAYINFO.psstUsed])
  const [selectedDate, setSelectedDate] = React.useState<Time | undefined>()
  const selectedCalendarInfo = selectedDate ? calendarInfo.get(selectedDate) : undefined
  const handleDateChange = React.useCallback((selection: TimeRange) => {
    const selectedDate = selection.getStart()
    setSelectedDate(selectedDate)
  }, [])
  const getCalendarInfoItems = React.useCallback(() => {
    if (!selectedCalendarInfo) {
      if (!selectedDate) {
        return (
          <>
            <SeattleCalendarItem dayInfo={CALENDARDAYINFO.seattleGigCompleted} />
            <SeattleCalendarItem dayInfo={CALENDARDAYINFO.psstUsed} />
          </>
        )
      }

      return (
        <Text.Body2>
          <em>
            <LocalizedText translationKey="dweb_seattle_dashboard_nothing_reported_day" />
          </em>
        </Text.Body2>
      )
    }

    return selectedCalendarInfo.map((dayInfo) => (
      <SeattleCalendarItem key={`calendar-item-${dayInfo.info}`} dayInfo={dayInfo} />
    ))
  }, [selectedCalendarInfo, selectedDate, CALENDARDAYINFO])

  return (
    <Container.Grid $gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'} $justifyItems="center" $gridGap="4rem">
      <Calendar minDay={JAN_01_2024} maxDaySpan={1} info={calendarInfo} onChange={handleDateChange} />
      <Container.GridItem $minHeight="11rem" $alignSelf="center">
        <Container.Grid $gridGap="1.2rem" $height="min-content">
          <Text.Subtitle1 $color="neutral-black">
            {selectedDate
              ? toLocalizedTime(selectedDate.date, selectedDate.zone).format({
                  month: 'long',
                  day: 'numeric',
                })
              : staticTextMap['Title']}
          </Text.Subtitle1>
          {getCalendarInfoItems()}
        </Container.Grid>
      </Container.GridItem>
    </Container.Grid>
  )
}

const SeattleCalendarItem: React.FC<{
  dayInfo: CalendarDayInfo
}> = (props) => {
  const { dayInfo } = props

  return (
    <Container.Grid $gridTemplateColumns="1.2rem 1fr" $gridGap="0.8rem">
      <Box.Simple $backgroundColor={dayInfo.color} $borderRadius="1.2rem" $height="1.2rem" $width="1.2rem" />
      <Text.Body2>{dayInfo.info}</Text.Body2>
    </Container.Grid>
  )
}

const WeeklyStatements: React.FC<{
  statements: WeeklyStatement[]
  isMobile: boolean
  staticTextMap: TextMap
}> = (props) => {
  const { statements, isMobile, staticTextMap = SeattleDashboardStaticText } = props
  const { formatStatementPeriodTitle } = useLocalization()

  return (
    <Box.Padding $padding={isMobile ? '0' : '0 0 2.4rem'}>
      {!statements.length ? (
        <Container.Grid $padding={isMobile ? '0' : '0 1.6rem'}>
          <Container.GridItemGrid
            $justifySelf="center"
            $justifyItems="center"
            $alignItems="center"
            $padding={isMobile ? '2.4rem' : '2.4rem 0 0'}
            $maxWidth="40rem"
            $textAlign="center"
          >
            <Text.Body1 $color="neutral-black">{staticTextMap['EmptySubtitle']}</Text.Body1>
            <Text.Body2>{staticTextMap['EmptyCaption']}</Text.Body2>
          </Container.GridItemGrid>
        </Container.Grid>
      ) : (
        <>
          <Box.Padding $padding="1.2rem 4rem">
            <Text.Overline>
              <LocalizedText translationKey="dweb_earnings_statement_period" />
            </Text.Overline>
          </Box.Padding>
          <Box.Border $borderTopWidth="1px" $borderTopStyle="solid" $borderTopColor="neutral-light" />
          {statements.map((statement, index) => (
            <>
              <Container.Grid
                $padding="1.2rem 2.4rem"
                $gridTemplateColumns="1fr auto"
                $justifyItems="space-between"
                $alignItems="center"
              >
                <Text.Body2 $color="neutral-black">{formatStatementPeriodTitle(statement.statement_period)}</Text.Body2>
                <DownloadWeeklyStatement statement={statement} isMobile={isMobile} />
              </Container.Grid>
              {index < statements.length - 1 ? (
                <Box.Model
                  $margin={isMobile ? '0' : '0 1.6rem'}
                  $borderTopWidth="1px"
                  $borderTopStyle="solid"
                  $borderTopColor="neutral-light"
                />
              ) : null}
            </>
          ))}
        </>
      )}
    </Box.Padding>
  )
}

const DownloadWeeklyStatement: React.FC<{
  statement: WeeklyStatement
  isMobile: boolean
}> = (props) => {
  const { statement, isMobile } = props
  const isWebview = useSelector<any, boolean>((state) => state.config.isWebview)
  const handleDownload = React.useCallback(() => {
    api.downloadFile({
      path: `/my_profile/earnings_statements/${statement.id}/download`,
      name: `weeklyStatement${statement.statement_period.title}.pdf`,
    })
  }, [statement.id, statement.statement_period.title])

  if (isWebview) {
    return (
      <AdaptiveExternalLink to={`download/seattleEarning/${statement.id}`}>
        <Icon.OpenInNewTab $width="2.4rem" />
      </AdaptiveExternalLink>
    )
  }
  return (
    <Button.Tertiary $color="primary-dark" onClick={handleDownload}>
      {isMobile ? <Icon.OpenInNewTab $width="2.4rem" /> : 'View'}
    </Button.Tertiary>
  )
}

export default SeattleDashboardValidator
